import { batch } from 'react-redux'
import { store } from '../../../index'
import { setClientAssignmentLastModified, setServerAssignmentLastModified } from '../../store/slices/assignment.slice'
import { actions as globalActions } from '../../store/reducers/global.reducer'
import { StatusCodes } from 'http-status-codes'
import MaintenanceService from '../../services/maintenance.service'
import AuthService from '../../services/auth.service'
import SessionStorageService from '../../services/session-storage.service'
import { actions as authActions } from '../../store/reducers/auth.reducer'

// Constants
const ERROR_MESSAGES = {
    TOKEN_EXPIRED: 'Could not validate credentials',
}

const MAX_RETRY_ATTEMPTS = 3

class TokenRefreshManager {
    constructor() {
        this.isRefreshing = false
        this.subscribers = []
    }

    addSubscriber(callback) {
        this.subscribers.push(callback)
    }

    notifySubscribers() {
        this.subscribers.forEach(callback => callback())
        this.subscribers = []
    }

    async handleTokenRefresh(error, axiosInstance) {
        const { response: errorResponse } = error
        
        // Track retry attempts for this request
        const retryCount = (errorResponse.config.__retryCount || 0) + 1
        
        // Check if we've exceeded the retry limit
        if (retryCount > MAX_RETRY_ATTEMPTS) {
            console.log(`Request exceeded retry limit (${MAX_RETRY_ATTEMPTS} attempts), returning token expired error`)
            const tokenExpiredError = new Error(ERROR_MESSAGES.TOKEN_EXPIRED)
            tokenExpiredError.response = {
                status: 401,
                data: { detail: ERROR_MESSAGES.TOKEN_EXPIRED }
            }
            return Promise.reject(tokenExpiredError)
        }

        const retryOriginalRequest = new Promise(resolve => {
            this.addSubscriber(() => {
                // Increment retry count for the next attempt
                errorResponse.config.__retryCount = retryCount
                resolve(axiosInstance(errorResponse.config))
            })
        })

        if (!this.isRefreshing) {
            this.isRefreshing = true

            try {
                await AuthService.refreshToken()
                this.notifySubscribers()
            } catch (refreshError) {
                // When refresh fails, reject instead of resolving
                return await this.handleRefreshError(refreshError)
            } finally {
                this.isRefreshing = false
            }
        }

        return retryOriginalRequest
    }

    async handleRefreshError(error) {
        console.error('Token refresh failed:', error)
        this.subscribers = []

        // Always handle unauthorized when refresh fails
        await this.handleUnauthorized()

        // Return a rejected promise to properly propagate the error
        return Promise.reject(error)
    }

    async handleUnauthorized() {
        const currentUser = store.getState().auth.user
        if (!currentUser) {
            return // Already logged out, no action needed
        }

        SessionStorageService.setWantedURL(window.location.pathname + window.location.search)
        await store.dispatch(authActions.logout())
    }
}

class MaintenanceChecker {
    constructor() {
        this.isChecking = false
    }

    async isUnderMaintenance(error) {
        if (error?.response?.status === StatusCodes.SERVICE_UNAVAILABLE) {
            return true
        }

        if (error?.message === 'Network Error' && !this.isChecking) {
            this.isChecking = true
            try {
                await MaintenanceService.healthCheck()
                return false
            } catch (error) {
                console.error('Health check failed:', error)
                return true
            } finally {
                this.isChecking = false
            }
        }
        return false
    }
}

const storeLastModifiedFromHeaders = response => {
    const lastModified = response?.headers?.['last-modified']
    if (lastModified) {
        batch(() => {
            store.dispatch(setClientAssignmentLastModified(lastModified))
            store.dispatch(setServerAssignmentLastModified(lastModified))
        })
    }
}

const getDetailFromResponseData = async data => {
    if (!data) return null

    if (typeof data === 'object' && !(data instanceof Blob)) {
        return data.detail
    }

    if (data instanceof Blob) {
        try {
            const text = await data.text()
            const parsed = JSON.parse(text)
            return parsed.detail
        } catch (error) {
            console.log('Failed to parse blob as JSON:', error)
            return null
        }
    }

    return null
}

const configureResponseInterceptors = axiosInstance => {
    const tokenManager = new TokenRefreshManager()
    const maintenanceChecker = new MaintenanceChecker()

    axiosInstance.interceptors.response.use(
        response => {
            storeLastModifiedFromHeaders(response)
            return response
        },
        async error => {
            console.log('Axios response interceptor caught error:', error)
            console.log('Error details:', {
                url: error.config?.url,
                method: error.config?.method,
                status: error.response?.status,
                statusText: error.response?.statusText,
                data: error.response?.data,
            })

            if (await maintenanceChecker.isUnderMaintenance(error)) {
                console.log('Server is under maintenance, setting maintenance mode')
                store.dispatch(globalActions.setIsServerOnMaintenance(true))
            }

            // Prevent infinite loop: Don't try to refresh if the refresh token request itself failed
            const isRefreshTokenRequest = error.config?.url?.includes('/auth/refresh-token')

            console.log(error.response?.data)

            // Handle both JSON and Blob responses for token expiration check
            const detail = await getDetailFromResponseData(error.response?.data)

            if (detail === ERROR_MESSAGES.TOKEN_EXPIRED && !isRefreshTokenRequest) {
                console.log('Token expired, attempting refresh')
                return tokenManager.handleTokenRefresh(error, axiosInstance)
            }

            console.log('Axios interceptor re-throwing error')
            return Promise.reject(error)
        },
    )
}

export default configureResponseInterceptors
